/**
 * collectionFilters.js
 *
 * @version 1.0.0
 */

import { initProductForms } from './productForm.js';
import { applyView, loadView } from './switch.js';

const DEBOUNCE_MS = 150; // to wait (ms) after the last change before firing a new AJAX request.

/* ----------------------------------------------------------------------- */
/*  DOM elements                                                           */
/* ----------------------------------------------------------------------- */
const SEL = {
	root: '[data-filters]',             // wrapper div – mount point
	form: '[data-filters-form]',        // <form>
	input: '[data-filters-input]',      // checkboxes / radios
	grid: '[data-filters-grid]',        // product grid
	pagination: '[data-pagination]',    // pagination nav
	activeBox: '[data-filters-active]', // badge container
	clear: '[data-filters-clear]',      // “Remove all”
	count: '[data-filters-count]',      // header results count
	badgeBtn: '.filters__btn',          // badge styling class
	sortCurrent: '[data-sort-current]', // inline "Sort by: <current>" target
	toggle: '[data-filters-toggle]',    // "Filters" open button
};

let debounceId;  // ID of the current debounce timer
let activeAbort; // Active AbortController for fetch()


/* ----------------------------------------------------------------------- */
/*  Event listeners                                                        */
/* ----------------------------------------------------------------------- */

function attachListeners(root) {
	// All input & sort <select> changes go through one capture listener
	root.addEventListener('change', onChange, true);

	// Click delegation for badges & “remove all”
	root.addEventListener('click', onClick);
}

/* ----------------------------------------------------------------------- */
/*  Event handlers                                                         */
/* ----------------------------------------------------------------------- */

/**
 * Handle any change inside the filters block:
 * – checkbox / radio  → update badges + debounce fetch
 *
 * * @param {Event} event
 */
function onChange(e) {
	const root = e.target.closest(SEL.root);
	if (!root) return;

	let needsFetch = false;

	if (e.target.matches(SEL.input)) {
		renderBadges(root);
		needsFetch = true;
	}

	// Update inline "Sort by" label immediately when sort changes
	if (e.target.matches(`${SEL.input}[name="sort_by"]`)) updateSortLabel(root);

	if (needsFetch) debounceUpdate(root);
}

/**
 * Delegated click handling for:
 * – badge (×) buttons
 * – “Remove all”
 *
 * * @param {Event} event
 */
function onClick(e) {
	const root = e.target.closest(SEL.root);
	if (!root) return;

	// Badge remove
	const badge = e.target.closest(SEL.badgeBtn);
	if (badge) {
		const form = root.querySelector(SEL.form);
		const keySel = CSS.escape(badge.dataset.key);
		const valSel = CSS.escape(badge.dataset.val);

		const input = form.querySelector(`${SEL.input}[name="${keySel}"][value="${valSel}"]`);
		if (input) input.checked = false;

		renderBadges(root);
		debounceUpdate(root);
		return;
	}

	// “Remove all”
	if (e.target.closest(SEL.clear)) {
		e.preventDefault();
		root.querySelectorAll(`${SEL.input}:checked`).forEach((el) => (el.checked = false));
		renderBadges(root);
		debounceUpdate(root);
	}
}

/* ----------------------------------------------------------------------- */
/*  Core helpers                                                           */
/* ----------------------------------------------------------------------- */

/**
 * Render the active filter badges and toggle the “Remove all” link.
 *
 * @param {HTMLElement} root
 */
function renderBadges(root) {
	const box = root.querySelector(SEL.activeBox);
	const clear = root.querySelector(SEL.clear);
	const form = root.querySelector(SEL.form);

	box.innerHTML = '';
	const frag = document.createDocumentFragment();

	// Gather all checked inputs
	const checked = Array.from(form.querySelectorAll(`${SEL.input}:checked`));

	// Skip sort radios when rendering badges
	const filtered = checked.filter((el) => el.name !== 'sort_by');

	if (filtered.length > 0) {
		// Only append this once
		const title = document.createElement('p');
		title.className = 'filters__current';
		title.textContent = 'Current Filters:';
		frag.appendChild(title);

		// Then create a badge for each checked input
		filtered.forEach((input) => {
			const label = input.closest('label');
			const text = Array.from(label.childNodes).find((n) => n.nodeType === 3 && n.textContent.trim())?.textContent.trim() || input.value;

			const btn = document.createElement('button');
			btn.type = 'button';
			btn.className = 'filters__btn';
			btn.dataset.key = input.name;
			btn.dataset.val = input.value;
			btn.innerHTML = `${text} &times;`;

			frag.appendChild(btn);
		});
	}

	box.appendChild(frag);
	clear.hidden = !filtered.length;

	// Update the global “Filters” toggle indicator (dot) and its accessible label.
	syncFiltersToggleIndicator(countSelectedFilters());
}

/**
 * Update the inline "Sort by" label to the checked radio’s label text.
 *
 * @param {HTMLElement} root
 */
function updateSortLabel(root) {
	const target = root.querySelector(SEL.sortCurrent);
	if (!target) return;

	const checked = root.querySelector(`${SEL.input}[name="sort_by"]:checked`);
	if (!checked) return;

	const label = checked.closest('label');
	if (!label) return;

	// Prefer only text nodes (avoid any nested elements if added later)
	const text = Array.from(label.childNodes)
		.filter((n) => n.nodeType === Node.TEXT_NODE)
		.map((n) => n.textContent)
		.join(' ')
		.replace(/\s+/g, ' ')
		.trim() || checked.value;
	target.textContent = text;
}

/**
 * Read total results from the (newly rendered) grid and update the header count.
 * Uses the server-provided `paginate.items` exposed via `data-results-count`.
 */
function updateFiltersCountFromGrid() {
	const grids = document.querySelectorAll(SEL.grid);
	if (!grids.length) return;

	const countEls = document.querySelectorAll(SEL.count);
	if (!countEls.length) return;

	// Prefer the first grid on the page (there should be only one)
	const grid = grids[0];
	const total = parseInt(grid.dataset.resultsCount, 10);
	if (Number.isNaN(total)) return;

	countEls.forEach((el) => (el.textContent = `(${total})`));
}

/**
 * Lightweight wrapper that debounces `updatePage()`.
 *
 * @param {HTMLFormElement} root
 */
function debounceUpdate(root) {
	clearTimeout(debounceId);
	debounceId = setTimeout(() => fetchAndSwap(root), DEBOUNCE_MS);
}

/**
 * Build query-string from form + sort.
 *
 * @param   {HTMLFormElement} root
 * @returns {string} Eg. "color=blue&size=medium&sort_by=price-desc"
 */
function buildQuery(root) {
	const form = root.querySelector(SEL.form);
	const params = new URLSearchParams(new FormData(form));

	/* ---------------------------------------------------------------
	 * Radios inside an element that becomes `inert` after closing
	 * (our menu pattern) are **excluded** from FormData, so the
	 * active `sort_by` value never makes it into the query‑string.
	 * Pull the checked one manually and force‑set it.
	 * ------------------------------------------------------------- */
	const sortChecked = root.querySelector(
		`${SEL.input}[name="sort_by"]:checked`
	);

	if (sortChecked) params.set('sort_by', sortChecked.value);

	return params.toString();
}

/**
 * Count unique selected filters (excludes sort_by) across the whole document.
 * Accounts for the same selection appearing in multiple filter UIs (dialog + inline).
 *
 * @returns {number}
 */
function countSelectedFilters() {
	const seen = new Set();
	document.querySelectorAll(`${SEL.input}:checked`).forEach((el) => {
		if (el.name === 'sort_by') return;
		seen.add(`${el.name}=${el.value}`);
	});
	return seen.size;
}

/**
 * Toggle a small indicator dot on every [data-filters-toggle] when filters are active.
 * Also keeps an accessible label in sync: "Filters (N selected)".
 *
 * @param {number} count
 */
function syncFiltersToggleIndicator(count) {
	const hasActive = count > 0;
	document.querySelectorAll(SEL.toggle).forEach((btn) => {
		// Visual state
		btn.toggleAttribute('data-has-active', hasActive);

		// Accessible label
		const base = btn.dataset.baseLabel
			|| btn.getAttribute('aria-label')
			|| (btn.textContent || '').trim()
			|| 'Filters';
		if (!btn.dataset.baseLabel) btn.dataset.baseLabel = base;
		btn.setAttribute('aria-label', hasActive ? `${base} (${count} selected)` : base);
	});
}

/**
 * Fetch the updated collection HTML, replace grid **and** pagination,
 * update history, then keep listeners alive (delegation means we don’t
 * need to re‑bind).
 *
 * @param {HTMLElement} root
 * @param {string}      [overrideUrl] – when provided (e.g. a pagination link)
 *                                     this URL is used verbatim.
 */
async function fetchAndSwap(root, overrideUrl = null) {
	// Cancel any previous request
	activeAbort?.abort();
	activeAbort = new AbortController();

	const url = overrideUrl || `${location.pathname}${(() => {
		const qs = buildQuery(root);
		return qs ? `?${qs}` : '';
	})()}`;

	try {
		// root.querySelector(SEL.grid).style.opacity = 0.6;
		document.querySelector(SEL.grid).style.opacity = 0.6;


		const res = await fetch(url, {
			headers: { 'X-Requested-With': 'XMLHttpRequest' },
			signal: activeAbort.signal,
		});

		const html = await res.text();
		const doc = new DOMParser().parseFromString(html, 'text/html');

		/* ------------------------- swap product grid ------------------------ */
		const newGrid = doc.querySelector(SEL.grid);
		document.querySelector(SEL.grid).replaceWith(newGrid);

		/* --------------------- update header results count ------------------ */
		updateFiltersCountFromGrid();

		/* ------------------------- swap pagination -------------------------- */
		const newPagination = doc.querySelector(SEL.pagination);
		const oldPagination = root.querySelector(SEL.pagination);

		if (oldPagination && newPagination) {
			oldPagination.replaceWith(newPagination);
		} else if (oldPagination && !newPagination) {
			oldPagination.remove();
		} else if (!oldPagination && newPagination) {
			newGrid.after(newPagination);
		}

		// Re‑init any JS inside product cards
		initProductForms();

		// Re-apply the grid/card layout the shopper picked earlier
		applyView(loadView());

		// Update browser history
		history.replaceState({}, '', url);

		// Badges rely on the new form; re‑render
		renderBadges(root);

		// Keep the	visible "Sort by" label in sync
		updateSortLabel(root);

		// Ensure the indicator reflects the latest state after swap
		syncFiltersToggleIndicator(countSelectedFilters());
	} catch (err) {
		if (err.name !== 'AbortError') window.location.href = url; // hard fallback
	} finally {
		activeAbort = null;
		root.querySelector(SEL.grid).style.opacity = 1;
	}
}

/* ----------------------------------------------------------------------- */
/*  Public API                                                             */
/* ----------------------------------------------------------------------- */

/**
 * Wire up a collection-filters block.
 *
 * @return {() => void} – call to detach
 */
export function initCollectionFilters() {
	// Initial badge render on all filter blocks
	document.querySelectorAll(SEL.root).forEach((root) => {
		renderBadges(root);
		updateSortLabel(root);
	});

	// Sync the header count with what's currently rendered
	updateFiltersCountFromGrid();

	// Initial indicator state on the Filters toggle
	syncFiltersToggleIndicator(countSelectedFilters());

	// One set of delegated listeners is enough
	if (!initCollectionFilters._bound) {
		document.addEventListener('change', onChange, true);
		document.addEventListener('click', onClick);
		initCollectionFilters._bound = true;
	}

	// Return a no‑op detach helper for parity
	return () => { };
}
